/* eslint-disable import/no-unresolved */
/* eslint-disable import/extensions */

import * as React from "react";
import { datadogRum } from "@datadog/browser-rum";
import { format } from "date-fns";
import { I18n } from "i18n-js";
import * as locales from "../../../../locales.json";
const i18n = new I18n(locales);
i18n.defaultLocale = "en";
i18n.locale = (window as any).locale || "";
import styled from "styled-components";

import * as API from "../../../../api/billing";
import DatePicker from "../../../reusable/datePicker";
import { CatalogOverrideModalBodyPropTypes } from "../lib/types";
import {
  StyledButton,
  StyledButtonContainer,
  StyledModalBody,
  StyledModalContents,
} from "../lib/styles";

const StyledLabel = styled.label`
  margin-left: 8px;
  margin-bottom: 11px;
`;

const StyledDiv = styled.div`
  margin-top: 24px;
`;

const StyledSelectEndDateDiv = styled.div`
  margin-top: 24px;
  font-size: 12px;
`;

const StyledNoteDiv = styled.div`
  margin-top: 24px;
`;

const CatalogOverrideModalBody = ({
  closeModal,
  catalogStatus,
  fetchData,
  overrideDate,
  catalogShutoff,
}: CatalogOverrideModalBodyPropTypes): React.ReactElement => {
  const today = new Date();

  // NOTE: This is type `any` because MUI takes 2 different types for `value` and `handleChange`
  const [localOverrideDate, setLocalOverrideDate] = React.useState<any>(null);

  const [stopOverride, setStopOverride] = React.useState<boolean>(null);

  const handleOverrideChange = (): void => {
    setStopOverride(!stopOverride);
  };

  const handleCatalogOverride = async (): Promise<void> => {
    try {
      if (stopOverride) {
        await API.updateCatalogOverride(null);
      } else {
        const formattedOverrideDate = format(localOverrideDate, "yyyy-MM-dd");
        await API.updateCatalogOverride({
          override_date: formattedOverrideDate,
        });
      }

      fetchData();
      closeModal();
    } catch (error) {
      datadogRum.addError(error);
    }
  };

  return (
    <StyledModalBody data-testid="catalog-override-modal-body">
      <StyledModalContents>
        {catalogShutoff && (
          <div className="alert alert-danger">
            <i className="fa fa-exclamation-triangle" style={{marginRight: "5px"}}/>
              Finance has turned off this catalog. Please check for payment and re-enable before attempting to override if still necessary.
          </div>
        )}

        <div>
          {" "}
          {i18n.translate("admin_pages.activity.catalog_override.status") +
            catalogStatus}
        </div>
        <div>
          {i18n.translate("admin_pages.activity.catalog_override.override")}:
          {overrideDate
            ? i18n.translate(
                "admin_pages.activity.catalog_override.on_through"
              ) + overrideDate
            : i18n.translate("admin_pages.activity.catalog_override.not_setup")}
        </div>

        {overrideDate && (
          <StyledDiv>
            <input
              data-testid="override-checkbox"
              id="override-checkbox"
              name="override-checkbox"
              onChange={handleOverrideChange}
              style={{
                marginTop: "0px",
                marginBottom: "2px",
                verticalAlign: "middle",
              }}
              type="checkbox"
            />
            <StyledLabel htmlFor="override-checkbox">
              {i18n.translate(
                "admin_pages.activity.catalog_override.stop_override"
              )}
            </StyledLabel>
          </StyledDiv>
        )}

        {!overrideDate && (
          <StyledSelectEndDateDiv>
            Select an end date for this override. This date can be modified at
            any time.
          </StyledSelectEndDateDiv>
        )}

        <DatePicker
          disabled={stopOverride || catalogShutoff}
          handleChange={setLocalOverrideDate}
          id="local-date"
          label={
            overrideDate
              ? i18n.translate(
                  "admin_pages.activity.catalog_override.date_picker.modify"
                )
              : i18n.translate(
                  "admin_pages.activity.catalog_override.date_picker.add"
                )
          }
          minDate={today}
          minDateMessage={i18n.translate(
            "admin_pages.activity.export_csv_modal.end_date_min_message"
          )}
          value={localOverrideDate}
        />

        <StyledNoteDiv>
          {i18n.translate("admin_pages.activity.catalog_override.note")}
        </StyledNoteDiv>
      </StyledModalContents>

      <StyledButtonContainer>
        <StyledButton
          onClick={closeModal}
          size="small"
          testId="cancel-button"
          variant="secondary"
        >
          {i18n.translate("admin_pages.activity.feedback_modal.cancel")}
        </StyledButton>
        <StyledButton
          disabled={(stopOverride == null && localOverrideDate == null) || catalogShutoff}
          onClick={handleCatalogOverride}
          size="small"
          testId="catalog-override-button"
          variant="primary"
        >
          {overrideDate
            ? i18n.translate(
                "admin_pages.activity.catalog_override.modal_button.modify"
              )
            : i18n.translate(
                "admin_pages.activity.catalog_override.modal_button.add"
              )}
        </StyledButton>
      </StyledButtonContainer>
    </StyledModalBody>
  );
};

export default CatalogOverrideModalBody;
